import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authService } from '../lib/services/auth';
import type { UserOrganizationsResponse } from '../lib/services/auth';
import { queryKeys } from '../lib/query/client';
import { AuthContext } from '../context/auth.context';
import type { MembershipOrg } from '../components/organizations/organizations.types';

function mapToMemberships(data: UserOrganizationsResponse): MembershipOrg[] {
  return data.approved.map((org) => ({
    id: org.id,
    name: org.name,
    slug: org.slug,
    roles: org.roles ?? [],
    canEnable: org.canEnable ?? false,
    enabled: org.enabled ?? true,
    tierName: org.tierName ?? '',
    memberCount: org.memberCount ?? 0,
    joinCode: org.joinCode ?? null,
    ownerContact: org.ownerContact ?? null,
    isManagedByPartner: org.isManagedByPartner ?? false,
    partnerName: org.partnerName ?? null,
  }));
}

export function useMyMemberships() {
  // Safe context access: the admin portal renders the shared Sidebar (and thus
  // this hook, via useNavigationContext) without an AuthProvider. Fall back to
  // unauthenticated — which disables the query — instead of throwing. Mirrors
  // the same guard in useNavigationContext.
  const isAuthenticated = useContext(AuthContext)?.isAuthenticated ?? false;

  const { data, isLoading } = useQuery({
    // Distinct key (prefix-extends queryKeys.organizations() so existing
    // invalidateQueries on that key still refreshes it) because this flow
    // explicitly opts into roles — the role-free managed flow must not share
    // the same cache entry and pick up role data.
    queryKey: [...queryKeys.organizations(), 'with-roles'],
    queryFn: () => authService.getUserOrganizations(true, { includeRoles: true }),
    select: mapToMemberships,
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
  });

  const memberships = data ?? [];

  return {
    memberships,
    isLoading,
    isEmpty: !isLoading && memberships.length === 0,
  };
}
